/**
 * RemoteConfigListener
 *
 * Mounts Remote Config for the app session. This is the component that turns the
 * Remote Config domain stack into live behaviour: it runs the init sequence once
 * at startup, forwards every resulting `RemoteConfigUpdateEffects` into the
 * `appOverlay` slice, and disposes the real-time subscription on unmount.
 *
 * Flutter sources of truth:
 * - `lib/presentation/screens/root/root_screen.dart:163-170` — `RootScreen._initApp`
 *   fires `RemoteConfigService.init()` at app boot, **before** `_initLoginFlow()`
 *   (`:172-174`).
 * - `lib/services/remote_config_service.dart:189-194` — `_handleConfigs()` and then
 *   the `onConfigUpdated` subscription. Both feed the *same* handler, which is why
 *   this component hands `InitializeRemoteConfigUseCase` a single `onEffects` sink
 *   for the post-init read and for every later push.
 *
 * **Ownership split.** This component only *dispatches*: `AppOverlayHost` renders
 * the budget-exceeded overlay / legal-document modals and owns the `pauseAll()`
 * side effect (Flutter `root_screen.dart:306-320`), while
 * `HandleRemoteConfigUpdateUseCase` owns the domain effects of a push (the
 * sync-cache invalidations). No domain code reaches into Redux and no UI code
 * re-implements the push semantics.
 *
 * **No auth gate — deliberate, do not "fix" it.** Unlike `WalletListener` /
 * `PremiumPackagesListener` / `LastSeenTimestampListener`, this listener must also
 * run for signed-out visitors: Flutter initializes Remote Config at
 * `root_screen.dart:166` *before* the login flow, and the budget-exceeded overlay
 * has to be reachable from the login screen. The effects that genuinely need a
 * signed-in user are auth-guarded one layer down, inside `resyncEntity`
 * (`syncSecureStorageUseCase`), which is where that guard belongs.
 *
 * **Double-mount safety.** `main.tsx:15-17` renders `<App />` **without**
 * `React.StrictMode`, so nothing in this codebase double-invokes the effect
 * intentionally — but a hot reload can still re-run it. That is safe by
 * construction: `remoteConfigService.init()` and `.subscribeToUpdates()` are both
 * idempotent, and `enqueueUpdatedLegalDocument` de-duplicates, so a re-run cannot
 * open a second subscription, stack a duplicate modal, or re-show an overlay the
 * user closed.
 *
 * **Known limitation (identical to Flutter's).** Remote Config values that arrive
 * after a component has rendered do **not** re-render it — every getter is a
 * synchronous static-style read. Only the three paths Flutter itself makes
 * reactive respond to a live push: budget-exceeded, sync-cache invalidation and
 * the legal-document modals.
 *
 * Renders nothing — mount once for the app session in `AppRoutes`, alongside
 * `AuthUserListener` / `WalletListener` / `LastSeenTimestampListener`.
 */
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { RemoteConfigUpdateEffects } from '@/domain/entities/remoteConfig/remoteConfigUpdateEffects';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { initializeRemoteConfigUseCase } from '@/domain/usecases/remoteConfig/initializeRemoteConfigUseCase';
import type { AppDispatch } from '@/presentation/store';
import {
  enqueueUpdatedLegalDocument,
  showBudgetExceededOverlay,
} from '@/presentation/store/slices/appOverlay/appOverlaySlice';

export const RemoteConfigListener = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // One sink for both Flutter effect sources (`_handleConfigs()` at
    // remote_config_service.dart:189 and the `onConfigUpdated` stream at :191).
    const onEffects = (effects: RemoteConfigUpdateEffects) => {
      // Flutter only raises the overlay on a `true` read
      // (remote_config_service.dart:335-339) — clearing the flag never hides it.
      if (effects.shouldDisplayBudgetExceededOverlay) {
        dispatch(showBudgetExceededOverlay());
      }

      // One modal per document whose version bumped, in arrival order
      // (remote_config_service.dart:234-310). `AppOverlayHost` renders the queue
      // head, so they present sequentially like Flutter's awaited dialogs.
      effects.updatedLegalDocuments.forEach((document) => {
        dispatch(enqueueUpdatedLegalDocument(document));
      });
    };

    // Fire-and-forget: `execute` never rejects (it contains its own try/catch),
    // so a Remote Config failure leaves the app running on compile-time defaults.
    void initializeRemoteConfigUseCase.execute(onEffects);

    // Flutter's `RemoteConfigService.close()` (remote_config_service.dart:327-329).
    // Flutter runs it on logout (root_screen.dart:262); the web keeps Remote Config
    // for the whole page session — it is app-scoped, not user-scoped — and tears it
    // down when this app-level listener unmounts.
    return () => {
      remoteConfigService.dispose();
    };
  }, [dispatch]);

  return null;
};
